import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";

import {
  useGetBenefitSettingsQuery,
  useUpdateBenefitSettingsMutation,
} from "../../store/services/benefits";
import { FormActions, FormField, FormRow, FormSection, FormToggle, PageHeader } from "../../components/ui";
import { useTenantScope } from "../../hooks/useTenantScope";
import { getAdminFacingApiErrorMessage } from "../../utils/functions/api-errors";
import { yupConstants } from "../../utils/constants/yup";
import { BenefitFeaturedBenefitsSection } from "./BenefitFeaturedBenefitsSection";
import { BenefitFeaturedEventsSection } from "./BenefitFeaturedEventsSection";

interface BenefitSettingsFormValues {
  showFeaturedEvents: boolean;
  featuredEventsMaxCount: number;
  featuredEventsTitle: string;
  showFeaturedBenefits: boolean;
  featuredBenefitsMaxCount: number;
  featuredBenefitsTitle: string;
}

export const BenefitSettings = () => {
  const tenantScope = useTenantScope();
  // Con scope global ("Todos") no hay un único tenant a configurar: el usuario debe
  // elegir uno en el selector superior, igual que en las FAQs destacadas de soporte.
  const needsTenantSelection = !tenantScope.loading && !tenantScope.locked;

  const { data: settings, isLoading } = useGetBenefitSettingsQuery(undefined, {
    skip: needsTenantSelection,
  });
  const [updateSettings, { isLoading: isSaving }] =
    useUpdateBenefitSettingsMutation();

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    resetForm,
    touched,
    errors,
    values,
    dirty,
  } = useFormik<BenefitSettingsFormValues>({
    initialValues: {
      showFeaturedEvents: settings?.showFeaturedEvents ?? false,
      featuredEventsMaxCount: settings?.featuredEventsMaxCount ?? 3,
      featuredEventsTitle:
        settings?.featuredEventsTitle ?? "Eventos que te pueden interesar",
      showFeaturedBenefits: settings?.showFeaturedBenefits ?? false,
      featuredBenefitsMaxCount: settings?.featuredBenefitsMaxCount ?? 4,
      featuredBenefitsTitle:
        settings?.featuredBenefitsTitle ?? "Beneficios destacados",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      featuredEventsMaxCount: Yup.number()
        .min(1, yupConstants(1).minNumber)
        .max(5, yupConstants(5).maxNumber)
        .required(yupConstants().required),
      featuredEventsTitle: Yup.string()
        .trim()
        .max(200, yupConstants(200).max)
        .required(yupConstants().required),
      featuredBenefitsMaxCount: Yup.number()
        .min(1, yupConstants(1).minNumber)
        .max(9, yupConstants(9).maxNumber)
        .required(yupConstants().required),
      featuredBenefitsTitle: Yup.string()
        .trim()
        .max(200, yupConstants(200).max)
        .required(yupConstants().required),
    }),
    onSubmit: ({
      showFeaturedEvents,
      featuredEventsMaxCount,
      featuredEventsTitle,
      showFeaturedBenefits,
      featuredBenefitsMaxCount,
      featuredBenefitsTitle,
    }) => {
      updateSettings({
        showFeaturedEvents,
        featuredEventsMaxCount,
        featuredEventsTitle: featuredEventsTitle.trim(),
        showFeaturedBenefits,
        featuredBenefitsMaxCount,
        featuredBenefitsTitle: featuredBenefitsTitle.trim(),
      })
        .unwrap()
        .then(() => toast.success("Configuración guardada"))
        .catch((err) => {
          toast.error(
            getAdminFacingApiErrorMessage(err, "No se pudo guardar la configuración")
          );
        });
    },
  });

  if (tenantScope.loading || isLoading) {
    return (
      <div className="p-card gap-4 p-3 flex justify-content-center p-5">
        <ProgressSpinner style={{ width: "50px", height: "50px" }} strokeWidth="8" />
      </div>
    );
  }

  return (
    <div className="p-card gap-4 p-3">
      <PageHeader title="Configuración de beneficios" />

      {needsTenantSelection ? (
        <div className="flex justify-content-center p-4">
          <Message
            severity="warn"
            text="Seleccioná un tenant en el selector superior para ver y editar su configuración de beneficios"
          />
        </div>
      ) : (
        <>
          <FormSection title="Configuración general" defaultOpen>
            {/* Por bloque: el switch en línea (FormToggle, como el mockup) y abajo
                el título y la cantidad, mitad y mitad. */}
            <form onSubmit={handleSubmit}>
              <h3 className="mt-0 mb-3 text-xl font-semibold">Beneficios destacados</h3>
              <FormRow>
                <FormToggle label="Mostrar en la pantalla de beneficios" htmlFor="showFeaturedBenefits">
                  <InputSwitch
                    inputId="showFeaturedBenefits"
                    checked={values.showFeaturedBenefits}
                    onChange={(e) =>
                      void setFieldValue("showFeaturedBenefits", Boolean(e.value))
                    }
                    disabled={isSaving}
                  />
                </FormToggle>
              </FormRow>
              <FormRow columns={2}>
                <FormField
                  label="Título de la sección"
                  htmlFor="featuredBenefitsTitle"
                  error={
                    touched.featuredBenefitsTitle && errors.featuredBenefitsTitle
                      ? errors.featuredBenefitsTitle
                      : undefined
                  }
                >
                  <InputText
                    invalid={!!touched.featuredBenefitsTitle && !!errors.featuredBenefitsTitle}
                    className="w-full"
                    id="featuredBenefitsTitle"
                    name="featuredBenefitsTitle"
                    placeholder="Ej: Beneficios destacados"
                    value={values.featuredBenefitsTitle}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isSaving || !values.showFeaturedBenefits}
                  />
                </FormField>
                <FormField
                  label="Cantidad máxima (1 a 9)"
                  htmlFor="featuredBenefitsMaxCount"
                  error={
                    touched.featuredBenefitsMaxCount && errors.featuredBenefitsMaxCount
                      ? errors.featuredBenefitsMaxCount
                      : undefined
                  }
                >
                  <InputNumber
                    invalid={
                      !!touched.featuredBenefitsMaxCount && !!errors.featuredBenefitsMaxCount
                    }
                    className="w-full"
                    inputClassName="w-full"
                    inputId="featuredBenefitsMaxCount"
                    name="featuredBenefitsMaxCount"
                    placeholder="Ej: 4"
                    value={values.featuredBenefitsMaxCount}
                    min={1}
                    max={9}
                    onValueChange={(e) =>
                      void setFieldValue("featuredBenefitsMaxCount", e.value ?? 1)
                    }
                    onBlur={handleBlur}
                    disabled={isSaving || !values.showFeaturedBenefits}
                  />
                </FormField>
              </FormRow>

              <h3 className="mt-2 mb-3 text-xl font-semibold">Eventos destacados</h3>
              <FormRow>
                <FormToggle label="Mostrar en la pantalla de beneficios" htmlFor="showFeaturedEvents">
                  <InputSwitch
                    inputId="showFeaturedEvents"
                    checked={values.showFeaturedEvents}
                    onChange={(e) =>
                      void setFieldValue("showFeaturedEvents", Boolean(e.value))
                    }
                    disabled={isSaving}
                  />
                </FormToggle>
              </FormRow>
              <FormRow columns={2}>
                <FormField
                  label="Título de la sección"
                  htmlFor="featuredEventsTitle"
                  error={
                    touched.featuredEventsTitle && errors.featuredEventsTitle
                      ? errors.featuredEventsTitle
                      : undefined
                  }
                >
                  <InputText
                    invalid={!!touched.featuredEventsTitle && !!errors.featuredEventsTitle}
                    className="w-full"
                    id="featuredEventsTitle"
                    name="featuredEventsTitle"
                    placeholder="Ej: Eventos que te pueden interesar"
                    value={values.featuredEventsTitle}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isSaving || !values.showFeaturedEvents}
                  />
                </FormField>
                <FormField
                  label="Cantidad máxima (1 a 5)"
                  htmlFor="featuredEventsMaxCount"
                  error={
                    touched.featuredEventsMaxCount && errors.featuredEventsMaxCount
                      ? errors.featuredEventsMaxCount
                      : undefined
                  }
                >
                  <InputNumber
                    invalid={
                      !!touched.featuredEventsMaxCount && !!errors.featuredEventsMaxCount
                    }
                    className="w-full"
                    inputClassName="w-full"
                    inputId="featuredEventsMaxCount"
                    name="featuredEventsMaxCount"
                    placeholder="Ej: 3"
                    value={values.featuredEventsMaxCount}
                    min={1}
                    max={5}
                    onValueChange={(e) =>
                      void setFieldValue("featuredEventsMaxCount", e.value ?? 1)
                    }
                    onBlur={handleBlur}
                    disabled={isSaving || !values.showFeaturedEvents}
                  />
                </FormField>
              </FormRow>

              <FormActions
                align="end"
                primaryLabel="Guardar cambios"
                loading={isSaving}
                disabled={isSaving || !dirty}
                disabledReason={!dirty ? "No hay cambios para guardar" : undefined}
                onCancel={() => resetForm()}
              />
            </form>
          </FormSection>

          <BenefitFeaturedBenefitsSection
            maxCount={settings?.featuredBenefitsMaxCount ?? 4}
          />

          <BenefitFeaturedEventsSection
            maxCount={settings?.featuredEventsMaxCount ?? 3}
          />
        </>
      )}
    </div>
  );
};
